import { useHistory, useLocation } from 'react-router-dom';
import ComingSoon from '../../../../components/ComingSoon';
import { mapClosedDeal, IN_SLICE_COLORS } from '../../../../service/seasonTransfers';
import UniverseTransferTable from './UniverseTransferTable';
import {
  StyledSeasonTabs,
  StyledSeasonPicker,
  StyledSeasonChip,
  StyledUniverseSection,
  StyledTextButton,
} from './styled';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FallbackContentAnimation from '../../../../components/FallbackContentAnimation';
import { TransactionsPreloader } from '../../../../components/ContentPreloaders';
import {
  transfersListRequest,
  transferShowUnshowFilter,
} from '../../../../redux/actions/Transfers';
import { leagueCreateCalendar } from '../../../../redux/actions/League';
import { teamAddToList } from '../../../../redux/actions/Team';
import { sidebarOpenRightPanel } from '../../../../redux/actions/Sidebar';
import { getTransfersList } from '../../../../redux/selector/Transfers';
import { calendarData } from '../../../../redux/selector/League';
import { dataForSidebar } from '../../../../redux/selector/Sidebar';
import { getSeasonNumber } from '../../../../redux/selector/Team';
import { cashCompact } from '../../../../service/functions';
import {
  splitSeasonTransfers,
  totalFees,
  groupDealsByTransferSeason,
} from '../../../../service/seasonTransfers';
import TransferDonut from './TransferDonut';
import {
  StyledPage,
  StyledTitleRow,
  StyledNet,
  StyledNetLabel,
  StyledNetValue,
  StyledIntro,
  StyledBackLink,
  StyledColumns,
  StyledSection,
  StyledHead,
  StyledTitle,
  StyledCount,
  StyledTableWrap,
  StyledTable,
  StyledTh,
  StyledTr,
  StyledTd,
  StyledPlayer,
  StyledSwatch,
  StyledPlayerName,
  StyledLoan,
  StyledClub,
  StyledClubName,
  StyledCrest,
  StyledCrestFallback,
  StyledFee,
  StyledEmpty,
} from './styled';

const ClubMark = ({ name, crest }) => (
  <StyledClub>
    {crest ? (
      <StyledCrest src={crest} alt="" />
    ) : (
      <StyledCrestFallback aria-hidden>
        {(name || '?').charAt(0)}
      </StyledCrestFallback>
    )}
    <StyledClubName>{name}</StyledClubName>
  </StyledClub>
);

const TransferTable = ({ rows, activeId, onHover, onOpen }) => {
  if (!rows.length) {
    return <StyledEmpty>No completed transfers yet</StyledEmpty>;
  }
  return (
    <StyledTableWrap>
      <StyledTable>
        <thead>
          <tr>
            <StyledTh>Player</StyledTh>
            <StyledTh>From</StyledTh>
            <StyledTh>To</StyledTh>
            <StyledTh $right>Fee</StyledTh>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <StyledTr
              key={row.id}
              $active={activeId === row.id}
              onClick={() => onOpen(row)}
              onMouseEnter={() => onHover(row)}
            >
              <StyledTd>
                <StyledPlayer>
                  <StyledSwatch $color={row.color} />
                  <StyledPlayerName>{row.playerName}</StyledPlayerName>
                  {row.isLoan ? <StyledLoan>{row.type}</StyledLoan> : null}
                </StyledPlayer>
              </StyledTd>
              <StyledTd>
                <ClubMark name={row.fromName} crest={row.fromCrest} />
              </StyledTd>
              <StyledTd>
                <ClubMark name={row.toName} crest={row.toCrest} />
              </StyledTd>
              <StyledTd $right>
                <StyledFee>{row.feeLabel}</StyledFee>
              </StyledTd>
            </StyledTr>
          ))}
        </tbody>
      </StyledTable>
    </StyledTableWrap>
  );
};

const SeasonColumn = ({ title, rows, activeId, onHover, onOpen }) => (
  <StyledSection>
    <StyledHead>
      <StyledTitle>{title}</StyledTitle>
      <StyledCount>
        {rows.length} deal{rows.length === 1 ? '' : 's'}
        {rows.length ? ` · ${cashCompact(totalFees(rows))}` : ''}
      </StyledCount>
    </StyledHead>
    <TransferDonut rows={rows} activeId={activeId} onSelect={onOpen} />
    <TransferTable
      rows={rows}
      activeId={activeId}
      onHover={onHover}
      onOpen={onOpen}
    />
  </StyledSection>
);

// Supply the complete current-season server feed here once its API is confirmed.
// null means unavailable; [] means a confirmed empty server history.
export default function SeasonTransfers({ universeTransfers = null }) {
  const location = useLocation();
  const history = useHistory();
  const tab =
    new URLSearchParams(location.search).get('view') === 'my-club'
      ? 'my-club'
      : 'universe';
  const [selectedClub, setSelectedClub] = useState(null);
  const [seasonKey, setSeasonKey] = useState('current');
  const chooseTab = (view) => {
    setSelectedClub(null);
    const params = new URLSearchParams(location.search);
    params.set('view', view);
    history.replace({ pathname: location.pathname, search: params.toString() });
  };
  const universeRows = useMemo(
    () =>
      Array.isArray(universeTransfers)
        ? universeTransfers
            .filter((deal) => Number(deal.status) === 1 && deal.player)
            .map((deal, index) =>
              mapClosedDeal(deal, null, index, IN_SLICE_COLORS)
            )
        : [],
    [universeTransfers]
  );
  const dispatch = useDispatch();
  const transfersList = useSelector(getTransfersList);
  const fixtures = useSelector(calendarData);
  const sidebar = useSelector(dataForSidebar);
  const seasonNumber = useSelector(getSeasonNumber);
  const loading = transfersList.isLoading && !transfersList.teamId;
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    dispatch(transfersListRequest({ silent: false }));
  }, [dispatch]);

  useEffect(() => {
    if (!fixtures?.matches?.length) {
      dispatch(leagueCreateCalendar());
    }
  }, [dispatch, fixtures?.matches?.length]);

  const seasonGroups = useMemo(
    () =>
      groupDealsByTransferSeason(
        selectedClub ? universeTransfers : transfersList.closed,
        fixtures?.matches,
        { timezone: fixtures?.timezone, seasonNumber }
      ),
    [
      selectedClub,
      universeTransfers,
      transfersList.closed,
      fixtures?.matches,
      fixtures?.timezone,
      seasonNumber,
    ]
  );

  useEffect(() => {
    const keys = seasonGroups.options.map((option) => option.key);
    if (!keys.includes(seasonKey)) {
      setSeasonKey(seasonGroups.defaultKey || 'current');
    }
  }, [seasonGroups, seasonKey]);

  const seasonDeals = seasonGroups.byKey[seasonKey] || seasonGroups.byKey.current || [];
  const activeSeasonLabel =
    seasonGroups.options.find((option) => option.key === seasonKey)?.label ||
    'This season';

  const { arrivals, departures } = useMemo(
    () =>
      splitSeasonTransfers(
        seasonDeals,
        selectedClub?._id || transfersList.teamId,
        selectedClub || sidebar?.user_team
      ),
    [
      seasonDeals,
      transfersList.teamId,
      sidebar?.user_team,
      selectedClub,
    ]
  );

  const spent = totalFees(arrivals);
  const received = totalFees(departures);
  const net = received - spent;
  const netTone = net > 0 ? 'plus' : net < 0 ? 'minus' : 'even';
  const netLabel = net > 0 ? `+${cashCompact(net)}` : cashCompact(net);

  const openPlayer = (row) => {
    if (!row?.playerId) return;
    setActiveId(row.id);
    dispatch(transferShowUnshowFilter(false));
    dispatch(sidebarOpenRightPanel(true));
    dispatch(teamAddToList({ _id: row.playerId, name: row.playerName }));
  };

  const showSeasonPicker =
    (tab === 'my-club' || selectedClub) && seasonGroups.options.length > 1;

  return (
    <StyledPage>
      <StyledBackLink to="/transfers/deals">← Closed deals</StyledBackLink>
      <StyledTitleRow>
        <h1>{selectedClub ? selectedClub.name : activeSeasonLabel}</h1>
        {(tab === 'my-club' || selectedClub) && (
          <StyledNet>
            <StyledNetLabel>Net</StyledNetLabel>
            <StyledNetValue $tone={netTone}>{netLabel}</StyledNetValue>
          </StyledNet>
        )}
      </StyledTitleRow>
      <StyledSeasonTabs role="tablist" aria-label="Transfer scope">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'universe'}
          onClick={() => chooseTab('universe')}
        >
          Universe
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'my-club'}
          onClick={() => chooseTab('my-club')}
        >
          My club
        </button>
      </StyledSeasonTabs>
      {selectedClub && (
        <StyledIntro>
          <StyledTextButton onClick={() => setSelectedClub(null)}>
            ← All universe transfers
          </StyledTextButton>
        </StyledIntro>
      )}
      {showSeasonPicker && (
        <StyledSeasonPicker role="tablist" aria-label="Season">
          {seasonGroups.options.map((option) => (
            <StyledSeasonChip
              key={option.key}
              type="button"
              role="tab"
              $active={seasonKey === option.key}
              aria-selected={seasonKey === option.key}
              onClick={() => setSeasonKey(option.key)}
            >
              {option.label}
              {option.count != null ? ` · ${option.count}` : ''}
            </StyledSeasonChip>
          ))}
        </StyledSeasonPicker>
      )}
      {tab === 'universe' && !selectedClub ? (
        <StyledUniverseSection>
          {universeTransfers === null ? (
            <ComingSoon>
              <StyledEmpty>Universe transfers</StyledEmpty>
            </ComingSoon>
          ) : (
            <UniverseTransferTable
              rows={universeRows}
              onPlayer={openPlayer}
              onClub={setSelectedClub}
            />
          )}
        </StyledUniverseSection>
      ) : (
        <>
          <StyledIntro>
            Spent {cashCompact(spent)} on arrivals · Received{' '}
            {cashCompact(received)} from departures. Click a player for details.
          </StyledIntro>
          <FallbackContentAnimation
            loading={loading}
            fallback={<TransactionsPreloader style={{ width: '100%' }} />}
          >
            <StyledColumns>
              <SeasonColumn
                title="Arrivals"
                rows={arrivals}
                activeId={activeId}
                onHover={(row) => setActiveId(row.id)}
                onOpen={openPlayer}
              />
              <SeasonColumn
                title="Departures"
                rows={departures}
                activeId={activeId}
                onHover={(row) => setActiveId(row.id)}
                onOpen={openPlayer}
              />
            </StyledColumns>
          </FallbackContentAnimation>
        </>
      )}
    </StyledPage>
  );
}
